import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import './LLMProvidersPage.css';

const PROVIDER_TYPE_OPTIONS = [
  { value: 'openai', label: 'OpenAI API' },
  { value: 'do_serverless', label: 'DO Gradient Serverless GPU' },
  { value: 'do_droplet', label: 'DO Hosted GPU Droplet' },
  { value: 'do_dedicated', label: 'DO Dedicated Inference GPU' },
  { value: 'runpod_serverless', label: 'RunPod Serverless (vLLM)' },
  { value: 'runpod_pod', label: 'RunPod Custom GPU Pod (vLLM)' },
];

const PROVIDER_ICONS = {
  openai: 'O',
  do_serverless: 'S',
  do_droplet: 'D',
  do_dedicated: 'G',
  runpod_serverless: 'R',
  runpod_pod: 'P',
};

const PROVIDER_LABELS = {
  openai: 'OpenAI',
  do_serverless: 'DO Serverless',
  do_droplet: 'DO Droplet',
  do_dedicated: 'DO Dedicated',
  runpod_serverless: 'RunPod Serverless',
  runpod_pod: 'RunPod Pod',
};

const EMPTY_FORM = {
  name: '',
  provider_type: 'openai',
  model_name: '',
  api_key: '',
  base_url: '',
  is_default: false,
  extra_config: {},
  embedding_model_name: '',
  embedding_base_url: '',
  embedding_api_key: '',
};

function ProviderFormModal({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...initial }));
  const [showEmbedding, setShowEmbedding] = useState(
    () => !!(initial?.embedding_model_name)
  );
  const isEdit = !!initial?.id;

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const baseUrlHint = {
    openai: 'Leave blank for default OpenAI API. Set for Azure or proxy endpoints.',
    do_serverless: 'Gradient serverless function URL, e.g. https://cloud.digitalocean.com/gen-ai/...',
    do_droplet: 'Droplet endpoint, e.g. http://<ip>:8000/v1',
    do_dedicated: 'Dedicated inference endpoint, e.g. https://...-public-dedicated-inference.do-infra.ai/v1',
    runpod_serverless: 'RunPod serverless OpenAI URL, e.g. https://api.runpod.ai/v2/<endpoint-id>/openai/v1',
    runpod_pod: 'RunPod pod proxy URL, e.g. https://<pod-id>-8000.proxy.runpod.net/v1',
  };

  const setCaCertPem = (e) => {
    setForm((f) => ({
      ...f,
      extra_config: { ...f.extra_config, ca_cert_pem: e.target.value },
    }));
  };

  return (
    <div className="llm-provider-modal-overlay" onClick={onCancel}>
      <div className="llm-provider-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit LLM Provider' : 'Add LLM Provider'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Provider Name</label>
            <input type="text" value={form.name} onChange={set('name')} placeholder="e.g. DeepSeek R1 on DO" required />
          </div>

          <div className="form-group">
            <label>Provider Type</label>
            <select value={form.provider_type} onChange={set('provider_type')}>
              {PROVIDER_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Model Name</label>
            <input type="text" value={form.model_name} onChange={set('model_name')} placeholder="e.g. deepseek-r1, gpt-4o" required />
            <div className="form-hint">The model identifier sent in API calls</div>
          </div>

          <div className="form-group">
            <label>API Key</label>
            <input
              type="password"
              value={form.api_key}
              onChange={set('api_key')}
              placeholder={isEdit ? '(unchanged if left blank)' : 'sk-...'}
            />
          </div>

          <div className="form-group">
            <label>Base URL</label>
            <input type="text" value={form.base_url} onChange={set('base_url')} placeholder="https://..." />
            <div className="form-hint">{baseUrlHint[form.provider_type]}</div>
          </div>

          {form.provider_type === 'do_dedicated' && (
            <div className="form-group">
              <label>CA Certificate (PEM)</label>
              <textarea
                value={form.extra_config?.ca_cert_pem || ''}
                onChange={setCaCertPem}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <div className="form-hint">
                Optional. Paste the CA certificate PEM content for private endpoints. Not needed for public endpoints.
              </div>
            </div>
          )}

          <div className="form-group">
            <label>
              <input type="checkbox" checked={form.is_default} onChange={set('is_default')} style={{ marginRight: 6 }} />
              Set as default provider
            </label>
          </div>

          {/* Embedding Configuration */}
          <div className="embed-section">
            <button
              type="button"
              className="embed-section-toggle"
              onClick={() => setShowEmbedding((v) => !v)}
            >
              {showEmbedding ? '▾' : '▸'} Embedding Configuration
            </button>

            {showEmbedding && (
              <div className="embed-section-body">
                <div className="form-hint embed-hint">
                  Leave empty to use the default OpenAI embedding model. Only set these if you are hosting a custom embedding model.
                </div>

                <div className="embed-warning">
                  Changing the embedding model requires re-compiling all graphs that were indexed with a different model, or queries will produce dimension mismatch errors.
                </div>

                <div className="form-group">
                  <label>Embedding Model Name</label>
                  <input
                    type="text"
                    value={form.embedding_model_name}
                    onChange={set('embedding_model_name')}
                    placeholder="e.g. text-embedding-3-large, bge-large-en-v1.5"
                  />
                </div>

                <div className="form-group">
                  <label>Embedding Base URL</label>
                  <input
                    type="text"
                    value={form.embedding_base_url}
                    onChange={set('embedding_base_url')}
                    placeholder="Leave blank to use same endpoint as chat"
                  />
                  <div className="form-hint">Separate embedding server URL, e.g. http://&lt;ip&gt;:8001/v1</div>
                </div>

                <div className="form-group">
                  <label>Embedding API Key</label>
                  <input
                    type="password"
                    value={form.embedding_api_key}
                    onChange={set('embedding_api_key')}
                    placeholder={isEdit ? '(unchanged if left blank)' : 'Leave blank to reuse chat API key'}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving || !form.name || !form.model_name}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LLMProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editProvider, setEditProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.listLLMProviders();
      setProviders(data.providers || []);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editProvider && !payload.api_key) {
        delete payload.api_key;
      }
      if (editProvider && !payload.embedding_api_key) {
        delete payload.embedding_api_key;
      }
      if (editProvider) {
        await apiService.updateLLMProvider(editProvider.id, payload);
      } else {
        await apiService.createLLMProvider(payload);
      }
      setShowModal(false);
      setEditProvider(null);
      fetchProviders();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete provider "${name}"?`)) return;
    try {
      await apiService.deleteLLMProvider(id);
      fetchProviders();
    } catch (err) {
      alert(err?.message || 'Delete failed');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await apiService.setDefaultLLMProvider(id);
      fetchProviders();
    } catch (err) {
      alert(err?.message || 'Failed to set default');
    }
  };

  const handleTest = async (id) => {
    setTestResults((prev) => ({ ...prev, [id]: { loading: true } }));
    try {
      const result = await apiService.testLLMProvider(id);
      setTestResults((prev) => ({ ...prev, [id]: result }));
    } catch (err) {
      setTestResults((prev) => ({ ...prev, [id]: { success: false, error: err?.message || 'Test failed' } }));
    }
  };

  const openEdit = (provider) => {
    setEditProvider(provider);
    setShowModal(true);
  };

  const openNew = () => {
    setEditProvider(null);
    setShowModal(true);
  };

  return (
    <div className="user-home-layout__page llm-providers-page">
      <div className="user-home-layout__page-header">
        <h1 className="user-home-layout__page-title">LLM Providers</h1>
        <p className="user-home-layout__page-subtitle">
          Configure LLM model deployments for graph building and query execution.
        </p>
      </div>

      <div className="user-home-layout__card">
        <div className="providers-toolbar">
          <h2 className="user-home-layout__section-title" style={{ margin: 0 }}>Configured Providers</h2>
          <button className="btn-add-provider" onClick={openNew}>+ Add Provider</button>
        </div>

        {loading && <div className="providers-loading">Loading providers...</div>}
        {error && <p style={{ color: '#ef4444' }}>{error}</p>}

        {!loading && !error && providers.length === 0 && (
          <div className="providers-empty">
            <div style={{ fontSize: 40, marginBottom: 8 }}>{'{ }'}</div>
            <p>No LLM providers configured yet.</p>
            <p>Add your first provider to get started.</p>
          </div>
        )}

        {!loading && providers.length > 0 && (
          <div className="providers-grid">
            {providers.map((p) => (
              <div key={p.id} className={`provider-card${p.is_default ? ' provider-card--default' : ''}`}>
                <div className="provider-icon">{PROVIDER_ICONS[p.provider_type] || '?'}</div>
                <div className="provider-info">
                  <div className="provider-header">
                    <h3 className="provider-name">{p.name}</h3>
                    <span className={`provider-badge provider-badge--${p.provider_type}`}>
                      {PROVIDER_LABELS[p.provider_type] || p.provider_type}
                    </span>
                    {p.is_default && <span className="provider-badge provider-badge--default">Default</span>}
                  </div>
                  <div className="provider-meta">
                    <span>Model: <strong>{p.model_name}</strong></span>
                    {p.embedding_model_name && <span>Embed: <strong>{p.embedding_model_name}</strong></span>}
                    {p.base_url && <span>URL: {p.base_url.length > 40 ? p.base_url.slice(0, 40) + '...' : p.base_url}</span>}
                    <span>Key: {p.api_key_masked || '(none)'}</span>
                  </div>
                  {testResults[p.id] && !testResults[p.id].loading && (
                    <div className={`test-result ${testResults[p.id].success ? 'test-result--success' : 'test-result--error'}`}>
                      {testResults[p.id].success
                        ? `Connected (${testResults[p.id].latency_ms}ms, ${testResults[p.id].tokens_used} tokens)`
                        : `Error: ${testResults[p.id].error}`}
                    </div>
                  )}
                </div>
                <div className="provider-actions">
                  <button className="btn-test" onClick={() => handleTest(p.id)} disabled={testResults[p.id]?.loading}>
                    {testResults[p.id]?.loading ? 'Testing...' : 'Test'}
                  </button>
                  <button onClick={() => openEdit(p)}>Edit</button>
                  {!p.is_default && <button onClick={() => handleSetDefault(p.id)}>Set Default</button>}
                  <button className="btn-delete" onClick={() => handleDelete(p.id, p.name)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <ProviderFormModal
          initial={editProvider ? {
            ...editProvider,
            api_key: '',
            embedding_api_key: '',
          } : null}
          onSave={handleSave}
          onCancel={() => { setShowModal(false); setEditProvider(null); }}
          saving={saving}
        />
      )}
    </div>
  );
}

export default LLMProvidersPage;
